import type { ComponentProps } from "react";

type UTMParameters = {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
};

type LaunchExternalStrategy = {
  shouldRenderLink(url: string): boolean;
  shouldLaunchExternally(url: string): boolean;
  getExternalLinkProps(url: string, utm?: UTMParameters): ComponentProps<"a">;
};

// TODO: Handle MLB links
const DEFAULT_LAUNCH_EXTERNAL_STRATEGY: LaunchExternalStrategy = {
  shouldRenderLink: (url) => hasSafeScheme(url),
  shouldLaunchExternally: (url) =>
    hasHTTPScheme(url) && hasDifferentOrigin(url),
  getExternalLinkProps: (url, utm = {}) => ({
    href: hasHTTPScheme(url) ? withUtmParameters(url, utm) : url,
    target: "_blank",
    rel: "noopener noreferrer",
  }),
};

const DEFAULT_UTM_PARAMETERS = {
  source: "pick3",
  campaign: "pick3",
} satisfies UTMParameters;

export function useExternalLinkProps(
  url: string | null,
  {
    utm,
  }: {
    utm: UTMParameters;
  } = { utm: {} },
) {
  if (!url) {
    return null;
  }

  const strategy = DEFAULT_LAUNCH_EXTERNAL_STRATEGY;

  if (!strategy.shouldRenderLink(url)) {
    return null;
  }

  if (strategy.shouldLaunchExternally(url)) {
    return strategy.getExternalLinkProps(url, utm);
  }

  return { href: url };
}

const UNSAFE_PROTOCOLS = [
  "blob:",
  "data:",
  "file:",
  "javascript:",
  "vbscript:",
];

function hasSafeScheme(href: string) {
  const url = new URL(href);
  const scheme = url.protocol;

  return !UNSAFE_PROTOCOLS.includes(scheme);
}

function hasHTTPScheme(href: string) {
  const url = new URL(href);
  const scheme = url.protocol;

  return scheme === "http:" || scheme === "https:";
}

function hasDifferentOrigin(href: string) {
  const url = new URL(href);
  return url.origin !== window.location.origin;
}

function withUtmParameters(
  href: string,
  utm: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
  },
) {
  const url = new URL(href);

  const utmWithDefaults = {
    ...DEFAULT_UTM_PARAMETERS,
    ...utm,
  };

  for (const [key, value] of Object.entries(utmWithDefaults)) {
    if (value && !url.searchParams.has(`utm_${key}`)) {
      url.searchParams.append(`utm_${key}`, value);
    }
  }

  return url.toString();
}
